import { Box, Link, Paper, Stack } from '@mui/material'
import axios from 'axios'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import CustomButton from '@/components/inputs/CustomButton'
import { useAlertContext } from '@/contexts/AlertContext'
import type { ConfigurationVersion } from '@/features/configuration/contexts/ConfigurationFileContext'
import { useConfigurationFileContext } from '@/features/configuration/contexts/ConfigurationFileContext'
import { ConfigVersionInfo } from '@/features/controller/configuration/components/ConfigVersionInfo'
import { useLatestControllerConfiguration } from '@/features/controller/configuration/hooks/useLatestControllerConfiguration'
import { ControllerConfigTypeId } from '@/features/controller/configuration/types'
import { ControllerRouteInformation } from '@/features/controller/constants'
import { useControllerRestartState } from '@/features/controller/restart/hooks/useControllerRestartState'
import { BindingPanel } from '@/features/resource/components/localControl/BindingPanel'
import { OperatingEnvelopeChart } from '@/features/resource/configuration/components/OperatingEnvelopeChart'
import { ResourceJsonErrorAlert } from '@/features/resource/configuration/components/ResourceJsonErrorAlert'
import { ResourceLocalConfigForm } from '@/features/resource/configuration/components/ResourceLocalConfigForm'
import { ResourceRestartStatusPanel } from '@/features/resource/configuration/components/ResourceRestartStatusPanel'
import type { ResourceJsonErrorCode } from '@/features/resource/configuration/endpoints/resourceJsonConfiguration'
import { useRestartControllerForResourceMutation } from '@/features/resource/configuration/hooks/useRestartControllerForResourceMutation'
import {
  parseBatteryResourceEntry,
  parseResourceConfigDocument,
} from '@/features/resource/configuration/utils/batteryResourceEntryGuard'
import { extractResourceJsonErrorCode } from '@/features/resource/configuration/utils/resourceJsonErrorCodes'
import type { ResourceLocalConfigFormValues } from '@/features/resource/configuration/utils/resourceLocalConfig'
import {
  applyLocalConfig,
  extractLocalConfig,
  findResourceEntry,
  getLocalConfigCreateDefaults,
  parseResourceJsonDocument,
} from '@/features/resource/configuration/utils/resourceLocalConfig'

type ResourceJsonEditorProps = {
  configVersion: ConfigurationVersion
  resourceId: string
  controllerId: string
  /** Battery control-port ID, used by the operating-envelope chart to query telemetry for the MWh
   * axis. `null` when the resource has no battery port — the chart falls back to %-only labels. */
  batteryControlPortId: string | null
  onConflict: () => void
}

export function ResourceJsonEditor({
  configVersion,
  resourceId,
  controllerId,
  batteryControlPortId,
  onConflict,
}: Readonly<ResourceJsonEditorProps>) {
  const { pushAlert } = useAlertContext()
  const { t } = useTranslation()

  const { updateAndInvalidate, isUpdatePending } = useConfigurationFileContext()
  const { restartController, isPending: isRestartingController } = useRestartControllerForResourceMutation(resourceId)
  const { setRestartInProgress } = useControllerRestartState(controllerId)

  const [errorCode, setErrorCode] = useState<ResourceJsonErrorCode | null>(null)

  // Single guarded parse → extract → entry-detect pass. A throw at any step (root parse, leaf
  // quantity parse via parseQuantity, etc.) routes to CorruptResourceJsonState. Pre-iter-29 only
  // the root parse was guarded — a malformed leaf like `socThresholds.maxSoc: "ninety-five"`
  // would render-throw and bounce the whole app to Error500 via the top-level ErrorBoundary.
  //
  // Empty `""` / whitespace-only is the legitimate fresh state (treat as parsed=null and route
  // to create-mode); a non-empty unparseable value is a corrupt document.
  type ParseResult = {
    parsed: ReturnType<typeof parseResourceJsonDocument> | null
    extracted: ResourceLocalConfigFormValues | null
    entryExists: boolean
    corrupt: boolean
  }
  const { parsed, extracted, entryExists, corrupt } = useMemo<ParseResult>(() => {
    const isEffectivelyEmpty = typeof configVersion.value === 'string' && configVersion.value.trim().length === 0
    try {
      const root = parseResourceJsonDocument(configVersion.value)
      return {
        parsed: root,
        extracted: extractLocalConfig(root, resourceId),
        entryExists: findResourceEntry(root, resourceId) !== null,
        corrupt: false,
      }
    } catch {
      return {
        parsed: null,
        extracted: null,
        entryExists: false,
        corrupt: !isEffectivelyEmpty,
      }
    }
  }, [configVersion.value, resourceId])

  const showCorruptDocument = corrupt

  // Mode detection: edit when `resources[]` has an entry for this resource AND that entry has a
  // steering block; otherwise create. Within create mode the battery picker is needed only when
  // no entry exists yet — when an entry exists without steering, we reuse the existing binding.
  const mode: 'edit' | 'create' = extracted ? 'edit' : 'create'
  const needsBatteryPicker = mode === 'create' && !entryExists

  // Create-mode battery picker reads app.json's `batteries[]`. The hook is keyed on (controllerId,
  // configTypeId) and dedupes via react-query; we only consume the result when the picker is needed.
  const appJsonQuery = useLatestControllerConfiguration(
    { controllerId, configTypeId: ControllerConfigTypeId.AppJson },
    { enabled: needsBatteryPicker },
  )
  const batteryOptions = useMemo(
    () => extractBatteryIds(appJsonQuery.latestConfigVersion?.value),
    [appJsonQuery.latestConfigVersion?.value],
  )

  function handleSaveError(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status
      if (status === 409) {
        pushAlert({ message: t('controllers.configuration.update_error_conflict'), severity: 'error' })
        onConflict()
        return false
      }
      if (status === 422) {
        const code = extractResourceJsonErrorCode(error.response?.data)
        if (code) {
          setErrorCode(code)
          return false
        }
      }
    }
    pushAlert({ message: t('controllers.configuration.update_error'), severity: 'error' })
    return false
  }

  async function save(values: ResourceLocalConfigFormValues): Promise<boolean> {
    if (!parsed) return false
    setErrorCode(null)

    // Build the next document BEFORE entering the network-error try so a local applyLocalConfig
    // throw (defensive: non-object intermediate at a known path, or create without batteryRef)
    // can't be mis-reported as a server failure — the request hasn't been sent yet.
    let nextDocument: ReturnType<typeof applyLocalConfig>
    try {
      nextDocument = applyLocalConfig(parsed, resourceId, values, {
        creationBatteryRef: needsBatteryPicker ? values.batteryRef : null,
      })
    } catch (error) {
      console.error('applyLocalConfig threw before PUT', error)
      pushAlert({
        message: t('controllers.configuration.corrupt_or_precondition_error'),
        severity: 'error',
      })
      return false
    }

    try {
      await updateAndInvalidate({
        previousVersionId: configVersion.id,
        value: JSON.stringify(nextDocument, null, 2),
      })
      pushAlert({ message: t('controllers.configuration.update_success'), severity: 'success' })
      return true
    } catch (error) {
      console.error('updateAndInvalidate rejected', error)
      return handleSaveError(error)
    }
  }

  async function onSaveAndRestart(values: ResourceLocalConfigFormValues) {
    const saveSucceeded = await save(values)
    if (!saveSucceeded) {
      return
    }
    try {
      await restartController()
      setRestartInProgress()
      pushAlert({ message: t('controllers.configuration.restart_requested'), severity: 'info' })
    } catch (error) {
      console.error('restartController rejected', error)
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const code = extractResourceJsonErrorCode(error.response?.data)
        if (code) {
          // Known 422 code: inline alert is the dedicated surface (ResourceJsonErrorAlert reads
          // `errorCode`). Suppress the generic toast — surfacing both would double-message a
          // single underlying failure.
          setErrorCode(code)
          return
        }
      }
      pushAlert({ message: t('controllers.configuration.restart_error'), severity: 'error' })
    }
  }

  // Split create-mode app.json states explicitly so users see why they can't proceed:
  // - loading → spinner
  // - fetch failed (network/5xx) → error panel with retry
  // - no app.json (404) or app.json present but `batteries[]` empty → "battery hardware not
  //   configured" CTA. On a brand-new controller the file simply doesn't exist yet, which is
  //   functionally the same situation as an empty batteries[]: the user needs to set hardware up
  //   on the controller-config page before binding a resource.
  const showAppJsonSpinner = needsBatteryPicker && appJsonQuery.isLoading
  const showAppJsonError = needsBatteryPicker && !appJsonQuery.isLoading && appJsonQuery.isError
  const noBatteriesConfigured =
    needsBatteryPicker && !appJsonQuery.isLoading && !showAppJsonError && batteryOptions.length === 0

  // In edit mode (an entry already exists), surface the LC-94 BindingPanel above the LC-191 form.
  // It's mounted with `editContext` so the inline BindingForm can write back via LC-94's
  // controller-config mutation; ignored in create mode. The LC-94 SteeringBasicsPanel (rest
  // setpoint + ramp speeds) was dropped — those values are already visible in the editable form
  // sections below, so showing them twice was redundant.
  const lc94EntryAndConfig = useMemo(() => {
    if (mode !== 'edit' || !parsed) return null
    const entry = findResourceEntry(parsed, resourceId)
    if (!entry) return null
    // BindingPanel (LC-94) reads `binding.batteryRef` directly without its own null-guard. The
    // Zod safeParse pair below validates the seam — the entry must have a string `id`/`batteryRef`
    // and `type === 'battery'`; the document must have a `resources[]`. Anything else (missing
    // binding, non-string ref, wrong type literal, malformed payload) skips the LC-94 binding UI
    // rather than letting BindingPanel crash on a runtime undefined.
    const resource = parseBatteryResourceEntry(entry)
    if (!resource) return null
    const fullConfig = parseResourceConfigDocument(parsed)
    if (!fullConfig) return null
    return { resource, fullConfig }
  }, [mode, parsed, resourceId])

  // ResourceLocalConfigForm now owns the 2-column layout (form on the LEFT, sticky sidebar on the
  // RIGHT) so the operating-envelope chart and the version/restart panels can share the form's
  // FormProvider. Pre-render the side stacks here and hand them in as slot content.
  const headerPanels = lc94EntryAndConfig ? (
    <Stack direction="column" spacing={3}>
      {errorCode && <ResourceJsonErrorAlert code={errorCode} controllerId={controllerId} />}
      <BindingPanel
        binding={lc94EntryAndConfig.resource.binding}
        editContext={{
          controllerId,
          resource: lc94EntryAndConfig.resource,
          fullConfig: lc94EntryAndConfig.fullConfig,
          previousVersionId: configVersion.id,
        }}
        resourceId={lc94EntryAndConfig.resource.id}
        resourceType={lc94EntryAndConfig.resource.type}
      />
    </Stack>
  ) : (
    errorCode && <ResourceJsonErrorAlert code={errorCode} controllerId={controllerId} />
  )

  const sidebar = (
    <Stack direction="column" spacing={2}>
      <OperatingEnvelopeChart batteryControlPortId={batteryControlPortId} />
      <ConfigVersionInfo configVersion={configVersion} controllerId={controllerId} />
      <ResourceRestartStatusPanel controllerId={controllerId} />
    </Stack>
  )

  // Corrupt resource.json branch — render an inline error panel and suppress the form entirely.
  // The Paper + sidebar shell matches the spinner / app-json branches so the surrounding tab
  // chrome stays consistent.
  if (showCorruptDocument) {
    return (
      <Box my={2}>
        <Stack alignItems="flex-start" direction={{ xs: 'column', md: 'row' }} gap={2}>
          <Box sx={{ flex: 6, width: '100%', maxWidth: { md: 640 }, minWidth: 0 }}>
            <Paper sx={{ p: 3 }}>
              <CorruptResourceJsonState controllerId={controllerId} />
            </Paper>
          </Box>
          <Box
            sx={{
              flex: 4,
              width: { xs: '100%', md: 'auto' },
              position: { md: 'sticky' },
              top: { md: 80 },
              alignSelf: { md: 'flex-start' },
            }}
          >
            <Stack direction="column" spacing={2}>
              <ConfigVersionInfo configVersion={configVersion} controllerId={controllerId} />
              <ResourceRestartStatusPanel controllerId={controllerId} />
            </Stack>
          </Box>
        </Stack>
      </Box>
    )
  }

  // Spinner / app-json error / empty-state branches don't render the form, so they don't need the
  // chart's FormProvider — fall back to the simpler single-column layout used pre-LC-191.
  if (showAppJsonSpinner || showAppJsonError || noBatteriesConfigured) {
    const onRetry = () => {
      appJsonQuery.refetch().catch(() => {
        /* errors surface via isError; nothing to do here */
      })
    }
    return (
      <Box my={2}>
        <Stack alignItems="flex-start" direction={{ xs: 'column', md: 'row' }} gap={2}>
          <Box sx={{ flex: 6, width: '100%', maxWidth: { md: 640 }, minWidth: 0 }}>
            <Paper sx={{ p: 3 }}>
              {errorCode && <ResourceJsonErrorAlert code={errorCode} controllerId={controllerId} />}
              {showAppJsonSpinner && <FullPageSpinner />}
              {showAppJsonError && <BatteryConfigErrorState controllerId={controllerId} onRetry={onRetry} />}
              {noBatteriesConfigured && <BatteryHardwareEmptyState controllerId={controllerId} />}
            </Paper>
          </Box>
          <Box
            sx={{
              flex: 4,
              width: { xs: '100%', md: 'auto' },
              position: { md: 'sticky' },
              // Clear the fixed MUI AppBar (~64 px) — see the matching note in
              // ResourceLocalConfigForm's sidebar Box.
              top: { md: 80 },
              alignSelf: { md: 'flex-start' },
            }}
          >
            <Stack direction="column" spacing={2}>
              <ConfigVersionInfo configVersion={configVersion} controllerId={controllerId} />
              <ResourceRestartStatusPanel controllerId={controllerId} />
            </Stack>
          </Box>
        </Stack>
      </Box>
    )
  }

  return (
    <Box my={2}>
      <ResourceLocalConfigForm
        key={configVersion.id}
        batteryOptions={batteryOptions}
        defaultValues={extracted ?? getLocalConfigCreateDefaults()}
        headerPanels={headerPanels}
        isSaving={isUpdatePending || isRestartingController}
        mode={mode}
        needsBatteryPicker={needsBatteryPicker}
        sidebar={sidebar}
        onSave={(values) => void save(values)}
        onSaveAndRestart={(values) => void onSaveAndRestart(values)}
      />
    </Box>
  )
}

/** Returns the list of battery ids declared in app.json (read-only cross-file read). Empty when
 * app.json is missing, unparseable, or has no `batteries[]`. */
function extractBatteryIds(value: string | undefined): string[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value) as { batteries?: Array<{ id?: unknown }> }
    if (!parsed || !Array.isArray(parsed.batteries)) return []
    return parsed.batteries.map((b) => (typeof b.id === 'string' ? b.id : '')).filter((id) => id.length > 0)
  } catch {
    return []
  }
}

function BatteryHardwareEmptyState({ controllerId }: Readonly<{ controllerId: string }>) {
  const { t } = useTranslation()
  return (
    <Stack direction="column" spacing={1}>
      <CustomTypography variant="h3">{t('resources.configuration.no_batteries.title')}</CustomTypography>
      <CustomTypography>{t('resources.configuration.no_batteries.body')}</CustomTypography>
      <Link
        component={RouterLink}
        to={ControllerRouteInformation.CONTROLLER_CONFIG_APP_JSON.navigationPath(controllerId)}
      >
        {t('resources.configuration.no_batteries.link')}
      </Link>
    </Stack>
  )
}

function CorruptResourceJsonState({ controllerId }: Readonly<{ controllerId: string }>) {
  const { t } = useTranslation()
  return (
    <Stack direction="column" spacing={1}>
      <CustomTypography variant="h3">{t('resources.configuration.resource_json_corrupt.title')}</CustomTypography>
      <CustomTypography>{t('resources.configuration.resource_json_corrupt.body')}</CustomTypography>
      <Link
        component={RouterLink}
        to={ControllerRouteInformation.CONTROLLER_CONFIG_RESOURCE_JSON.navigationPath(controllerId)}
      >
        {t('resources.configuration.resource_json_corrupt.link')}
      </Link>
    </Stack>
  )
}

function BatteryConfigErrorState({ controllerId, onRetry }: Readonly<{ controllerId: string; onRetry: () => void }>) {
  const { t } = useTranslation()
  return (
    <Stack direction="column" spacing={1}>
      <CustomTypography variant="h3">{t('resources.configuration.app_json_error.title')}</CustomTypography>
      <CustomTypography>{t('resources.configuration.app_json_error.body')}</CustomTypography>
      <Stack direction="row" spacing={2}>
        <CustomButton variant="contained" onClick={onRetry}>
          {t('resources.configuration.app_json_error.retry')}
        </CustomButton>
        <Link
          component={RouterLink}
          to={ControllerRouteInformation.CONTROLLER_CONFIG_APP_JSON.navigationPath(controllerId)}
        >
          {t('resources.configuration.app_json_error.link')}
        </Link>
      </Stack>
    </Stack>
  )
}
